// Event-paced goals: when someone is working toward a dated occasion (a
// wedding, a holiday), the daily budget offset is derived from that date
// instead of the fixed per-goal number — clamped hard, so an over-ambitious
// date can only ever produce "the fastest safe pace", never an unsafe plan.
// Pure date maths, shared by the client preview and the server truth, and by
// design returns null in every case where pacing shouldn't apply — callers
// then fall back to the standard offsets unchanged.

export const KCAL_PER_KG_TISSUE = 7700;
/** Steepest sustained paced deficit (≈0.68 kg/week). The absolute floors in
 *  tdee.ts / initialTargets still apply on top of this. */
export const MAX_DEFICIT_KCAL = 750;
/** Fastest paced surplus (≈0.36 kg/week of tissue) — beyond this is fat, not
 *  progress, whatever the date says. */
export const MAX_SURPLUS_KCAL = 400;
/** Events closer than this pace nothing: under two weeks, date-driven pacing
 *  would just mean crash-dieting with a deadline. */
export const MIN_EVENT_DAYS = 14;
export const MAX_EVENT_DAYS = 730;

export interface EventPacing {
  /** Signed daily offset vs maintenance, already clamped safe. */
  offset_kcal: number;
  days: number;
  /** Rounded for display copy. */
  weeks: number;
  /** true when the date asked for more than the safe cap allows — the UI is
   *  honest about it ("as fast as is safe") instead of pretending. */
  limited: boolean;
}

export function daysBetween(fromISO: string, toISO: string): number {
  return Math.round(
    (Date.parse(toISO + "T00:00:00Z") - Date.parse(fromISO + "T00:00:00Z")) / 86400000,
  );
}

export function eventPacing(args: {
  weight_kg: number;
  target_weight_kg: number;
  event_date: string | null | undefined; // YYYY-MM-DD
  today: string; // YYYY-MM-DD, user-local
  goal_type: string;
}): EventPacing | null {
  const { weight_kg, target_weight_kg, event_date, today, goal_type } = args;
  if (!event_date || !/^\d{4}-\d{2}-\d{2}$/.test(event_date)) return null;
  const days = daysBetween(today, event_date);
  if (!Number.isFinite(days) || days < MIN_EVENT_DAYS || days > MAX_EVENT_DAYS) return null;

  // Pacing only when the event pulls in the same direction as the chosen
  // goal — a mismatch (say "lose weight" with a target above today's weight)
  // falls back to the standard offsets rather than silently flipping signs.
  const delta = target_weight_kg - weight_kg; // negative = lose
  const losing = goal_type === "weight_loss" || goal_type === "cut";
  const gaining = goal_type === "bulk" || goal_type === "weight_gain";
  if (!losing && !gaining) return null;
  if (losing && delta >= 0) return null;
  if (gaining && delta <= 0) return null;

  const raw = (delta * KCAL_PER_KG_TISSUE) / days;
  const cap = delta < 0 ? -MAX_DEFICIT_KCAL : MAX_SURPLUS_KCAL;
  const limited = Math.abs(raw) > Math.abs(cap);
  return {
    offset_kcal: limited ? cap : Math.round(raw),
    days,
    weeks: Math.max(1, Math.round(days / 7)),
    limited,
  };
}

/** Safe sustained rates implied by the kcal caps (≈0.68 / ≈0.36 kg per week). */
export const SAFE_LOSS_KG_PER_WEEK = (MAX_DEFICIT_KCAL * 7) / KCAL_PER_KG_TISSUE;
export const SAFE_GAIN_KG_PER_WEEK = (MAX_SURPLUS_KCAL * 7) / KCAL_PER_KG_TISSUE;

/** For an over-ambitious occasion: what IS safely doable by the date, and how
 *  long the full change realistically needs — the numbers behind the UI's
 *  honest feedback and alternative suggestion. */
export function paceOutlook(deltaKg: number, days: number): {
  achievableKg: number;
  neededWeeks: number;
} {
  const rate = deltaKg < 0 ? SAFE_LOSS_KG_PER_WEEK : SAFE_GAIN_KG_PER_WEEK;
  return {
    achievableKg: Math.round(((rate * days) / 7) * 10) / 10,
    neededWeeks: Math.max(1, Math.ceil(Math.abs(deltaKg) / rate)),
  };
}
